
import React, { useEffect, useState, useRef } from "react";
import {
  Box,
  Typography,
  Button,
  Paper,
  Dialog,
  DialogContent,
  DialogActions,
  ToggleButtonGroup,
  ToggleButton,
} from "@mui/material";
import QRCode from "react-qr-code";
import JsBarcode from "jsbarcode";
import { useReactToPrint } from "react-to-print";
import PrintIcon from "@mui/icons-material/Print";
import DescriptionIcon from "@mui/icons-material/Description";

const MultiShipmentLabel = ({ isOpen, onReopenPrintModal, shipments }) => {
  const printRef = useRef();
  const [barcodeDataUrls, setBarcodeDataUrls] = useState({});
  const [printMode, setPrintMode] = useState("tsc"); // 'tsc' or 'a4'

  const InfoRow = ({ text }) => (
    <Box display="flex" gap={0.5} m={0} ml={0.5} lineHeight={1.1}>
      <Typography variant="body2" color="text.primary" fontSize={8}>
        {text || "—"}
      </Typography>
    </Box>
  );

  // Helper function to truncate product description
  const truncateDescription = (text, charLimit = 60) => {
    if (!text) return "N/A";
    const trimmed = text.trim();
    if (trimmed.length > charLimit) {
      return trimmed.substring(0, charLimit) + "...";
    }
    return trimmed;
  };

  useEffect(() => {
    const newBarcodeDataUrls = {};
    shipments?.forEach((shipment) => {
      if (shipment?.reference || shipment?.shipmentId) {
        try {
          const canvas = document.createElement("canvas");
          JsBarcode(canvas, shipment.reference || shipment.shipmentId, {
            format: "CODE128",
            displayValue: false,
            height: 30,
            width: 1.4,
            margin: 0,
          });
          newBarcodeDataUrls[shipment.reference || shipment.shipmentId] =
            canvas.toDataURL("image/png");
        } catch (error) {
          console.error("Barcode generation failed:", error);
        }
      }
    });
    setBarcodeDataUrls(newBarcodeDataUrls);
  }, [shipments]);

  const handlePrint = useReactToPrint({
    contentRef: printRef,
  });

  const handlePrintModeChange = (event, newMode) => {
    if (newMode !== null) {
      setPrintMode(newMode);
    }
  };

  if (!shipments || shipments.length === 0) return null;

  return (
    <Dialog open={isOpen} onClose={onReopenPrintModal} maxWidth="lg" fullWidth>
      <DialogContent>
        {/* Print Mode Selector */}
        <Box className="no-print" mb={2} display="flex" justifyContent="center">
          <ToggleButtonGroup
            value={printMode}
            exclusive
            onChange={handlePrintModeChange}
            aria-label="print mode"
            size="small"
          >
            <ToggleButton value="tsc" aria-label="tsc printer">
              <PrintIcon sx={{ mr: 1 }} fontSize="small" />
              TSC Printer (Label)
            </ToggleButton>
            <ToggleButton value="a4" aria-label="a4 printer">
              <DescriptionIcon sx={{ mr: 1 }} fontSize="small" />
              A4 Printer (3 per page)
            </ToggleButton>
          </ToggleButtonGroup>
        </Box>

        <Box ref={printRef}>
          {printMode === "tsc" ? (
            // ========== TSC MODE - 100% ORIGINAL CODE ==========
            <>
              {shipments.map((shipment, index) => (
                <Paper
                  key={index}
                  elevation={1}
                  sx={{
                    width: "98mm",
                    height: "74.676mm",
                    padding: "2mm",
                    mx: "auto",
                    border: "1px solid #000",
                    overflow: "hidden",
                    boxSizing: "border-box",
                    mb: index < shipments.length - 1 ? 2 : 0,
                    pageBreakAfter: "always",
                  }}
                >
                  {/* Header Section */}
                  <div style={{ marginBottom: "2px", position: "relative", minHeight: "58px" }}>
                    {/* QR code + date - positioned absolutely so they don't affect layout height */}
                    <div
                      style={{
                        position: "absolute",
                        top: 0,
                        right: 0,
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        gap: "1px",
                      }}
                    >
                      <QRCode value={shipment.shipmentId} size={46} />
                      <span style={{ fontSize: "7px", fontWeight: 600, whiteSpace: "nowrap" }}>
                        {shipment.createdAt
                          ? new Date(shipment.createdAt).toLocaleDateString("en-GB", {
                            day: "2-digit",
                            month: "2-digit",
                            year: "numeric",
                          })
                          : "N/A"}
                      </span>
                    </div>
                    <div
                      style={{
                        display: "flex",
                        alignItems: "flex-start",
                        justifyContent: "space-between",
                        gap: "8px",
                        marginBottom: "0px",
                        paddingRight: "52px",
                      }}
                    >
                      <div
                        style={{
                          fontSize: "9px",
                          fontWeight: 600,
                          flex: 1,
                          wordBreak: "break-word",
                          lineHeight: "1.2",
                        }}
                      >
                        {shipment?.entity?.name || "N/A"}
                      </div>
                    </div>
                    <div
                      style={{
                        fontSize: "13px",
                        fontWeight: 700,
                        textAlign: "center",
                        lineHeight: 1.2,
                        marginTop: "2px",
                        marginBottom: "1px",
                      }}
                    >
                      {shipment?.shipmentId}
                    </div>
                    <div
                      style={{
                        display: "flex",
                        flexDirection: "column",
                        gap: "0px",
                        marginTop: "1px",
                      }}
                    >
                      {/* Branch Route */}
                      <div
                        style={{
                          fontSize: "12px",
                          fontWeight: 700,
                          textAlign: "center",
                          wordBreak: "break-word",
                          lineHeight: "1.2",
                        }}
                      >
                        {`${shipment?.sourceBranch?.name || "N/A"} → `}
                        <span style={{ fontWeight: 700, fontSize: "12px" }}>
                          {shipment?.destinationBranch?.name || "N/A"}
                        </span>
                      </div>
                      {(shipment?.sourceBranch?.district?.name ||
                        shipment?.destinationBranch?.district?.name) && (
                          <div
                            style={{
                              fontSize: "9px",
                              fontWeight: 500,
                              textAlign: "center",
                              wordBreak: "break-word",
                              lineHeight: "1.2",
                              marginTop: "1px",
                              color: "#444",
                            }}
                          >
                            {`${shipment?.sourceBranch?.district?.name || "N/A"} - ${shipment?.destinationBranch?.district?.name || "N/A"
                              }`}
                          </div>
                        )}
                    </div>
                  </div>
                  <Box my={0.1} borderBottom="1px dotted #000" />
                  {/* From / To Section */}
                  <Box
                    display="flex"
                    justifyContent="center"
                    fontSize={8}
                    mb={0.25}
                    lineHeight={1}
                  >
                    <Box width="48%">
                      <Typography fontWeight={700} fontSize={10} gutterBottom sx={{ mb: 0.5 }}>
                        From
                      </Typography>
                      {shipment.vendor ? (
                        <>
                          <InfoRow
                            text={
                              <span className="text-[9px] font-bold" style={{ fontSize: "9px" }}>
                                {`Name: ${shipment.vendor.shopName}`}
                              </span>
                            }
                          />
                          <InfoRow
                            text={
                              <span className="text-[9px] font-bold" style={{ fontSize: "9px" }}>
                                {`Contact: ${shipment.vendor.phone}`}
                              </span>
                            }
                          />
                          <InfoRow
                            text={
                              <span className="text-[9px] font-bold" style={{ fontSize: "9px" }}>
                                {`Address: ${shipment.vendor.address}`}
                              </span>
                            }
                          />
                          <InfoRow
                            text={
                              <span className="text-[9px] font-bold" style={{ fontSize: "9px" }}>
                                {`Payer (Sender): Rs. ${shipment.payerDetails?.sender?.totalCharge || 0
                                  }`}
                              </span>
                            }
                          />
                        </>
                      ) : (
                        <>
                          <InfoRow
                            text={
                              <span className="text-[9px] font-bold" style={{ fontSize: "9px" }}>
                                {`Name: ${shipment.customerId?.customerUser?.name || "N/A"}`}
                              </span>
                            }
                          />
                          <InfoRow
                            text={
                              <span className="text-[9px] font-bold" style={{ fontSize: "9px" }}>
                                {`Contact: ${shipment.customerId?.customerUser?.phone || "N/A"}`}
                              </span>
                            }
                          />
                          <InfoRow
                            text={
                              <span className="text-[9px] font-bold" style={{ fontSize: "9px" }}>
                                {`Address: ${shipment.customerId?.address || "N/A"}`}
                              </span>
                            }
                          />
                          <InfoRow
                            text={
                              <span className="text-[9px] font-bold" style={{ fontSize: "9px" }}>
                                {`Payer (Sender): Rs. ${shipment.payerDetails?.sender?.totalCharge || 0
                                  }`}
                              </span>
                            }
                          />
                        </>
                      )}
                    </Box>
                    <Box width="48%">
                      <Typography fontWeight={700} fontSize={10} gutterBottom sx={{ mb: 0.5 }}>
                        To
                      </Typography>
                      <InfoRow
                        text={
                          <span className="text-[9px] font-bold" style={{ fontSize: "9px" }}>
                            {`Name: ${shipment.receiver?.customerUser?.name || "N/A"}`}
                          </span>
                        }
                      />
                      <InfoRow
                        text={
                          <span className="text-[9px] font-bold" style={{ fontSize: "9px" }}>
                            {`Contact: ${shipment.receiver?.customerUser?.phone || "N/A"}`}
                          </span>
                        }
                      />
                      <InfoRow
                        text={
                          <span className="text-[9px] font-bold" style={{ fontSize: "9px" }}>
                            {`Address: ${shipment.receiver?.address || "N/A"}`}
                          </span>
                        }
                      />
                      <InfoRow
                        text={
                          <span className="text-[9px] font-bold" style={{ fontSize: "9px" }}>
                            {`Payer (Receiver): Rs. ${shipment.payerDetails?.receiver?.totalCharge || 0
                              }`}
                          </span>
                        }
                      />
                    </Box>
                  </Box>

                  <Box my={0.25} borderBottom="1px dotted #000" />

                  {/* Details Section */}
                  <Box className="" ml={1} mb={0.25}>
                    <Box
                      display="flex"
                      justifyContent="space-between"
                      alignItems="center"
                      lineHeight={1.2}
                    >
                      <span style={{ fontSize: "8px", fontWeight: 700 }}>Delivery Charge:</span>
                      <span style={{ fontSize: "8px", fontWeight: 700 }}>
                        Rs. {shipment.shipmentAmount || 0}
                      </span>
                    </Box>

                    {shipment?.weight && (
                      <Box display="flex" justifyContent="space-between" alignItems="center" lineHeight={1.2}>
                        <span style={{ fontSize: "8px", fontWeight: 700 }}>Shipment Weight:</span>
                        <span style={{ fontSize: "8px", fontWeight: 700 }}>
                          {shipment.weight} kg
                        </span>
                      </Box>
                    )}
                    {shipment?.unit?.value && shipment?.unit?.unitType?.name && (
                      <Box display="flex" justifyContent="space-between" alignItems="center" lineHeight={1.2}>
                        <span style={{ fontSize: "8px", fontWeight: 700 }}>
                          {shipment.unit.unitType.name.toLowerCase() === "weight"
                            ? "Shipment Weight:"
                            : "Shipment Unit:"}
                        </span>
                        <span style={{ fontSize: "8px", fontWeight: 700 }}>
                          {shipment.unit.unitType.name.toLowerCase() === "weight"
                            ? `${shipment.unit.value} kg`
                            : `${shipment.unit.value} ${shipment.unit.unitType.name}`}
                        </span>
                      </Box>
                    )}

                    <Box
                      display="flex"
                      justifyContent="space-between"
                      alignItems="center"
                      lineHeight={1.2}
                    >
                      <span style={{ fontSize: "8px", fontWeight: 700 }}>Shipment Type:</span>
                      <span style={{ fontSize: "8px", fontWeight: 700 }}>
                        {shipment?.shipmentDelivery || "N/A"}
                      </span>
                    </Box>
                  </Box>

                  <Box borderBottom="1px dotted #000" />

                  {/* Description Section */}
                  <Box ml={1} my={0.25} lineHeight={1.1}>
                    <Box>
                      <span style={{ fontSize: "9px", fontWeight: 700 }}>Description:</span>
                      <span
                        style={{
                          fontWeight: 700,
                          fontSize: "8px",
                          marginLeft: "8px",
                          wordBreak: "break-word",
                        }}
                      >
                        {truncateDescription(shipment?.productDescription, 60)}
                      </span>
                    </Box>
                  </Box>
                  {/* Instruction Section */}
                  <Box ml={1} my={0.15} lineHeight={1.1}>
                    <Box>
                      <span style={{ fontSize: "9px", fontWeight: 700 }}>Instruction:</span>
                      <span
                        style={{
                          fontWeight: 700,
                          fontSize: "8px",
                          marginLeft: "8px",
                          wordBreak: "break-word",
                        }}
                      >
                        {truncateDescription(shipment?.instruction, 60)}
                      </span>
                    </Box>
                  </Box>

                  <Box my={0.1} borderBottom="1px dotted #000" />
                  {/* Barcode Section */}
                  <Box mt={0.25} textAlign="center" style={{ overflow: "hidden" }}>
                    {barcodeDataUrls[shipment?.reference || shipment.shipmentId] ? (
                      <>
                        <img
                          src={barcodeDataUrls[shipment?.reference || shipment.shipmentId]}
                          alt="Barcode"
                          style={{
                            width: "auto",
                            height: "auto",
                            maxHeight: "35px",
                            objectFit: "contain",
                            margin: "0 auto",
                            display: "block",
                          }}
                        />
                        <Typography fontSize={10} fontWeight={600} lineHeight={1} marginTop={0.5}>
                          {shipment?.reference || shipment.shipmentId}
                        </Typography>
                      </>
                    ) : (
                      <Typography fontSize={8}>Barcode not generated</Typography>
                    )}
                  </Box>
                </Paper>
              ))}
            </>
          ) : (
            // ========== A4 MODE ==========
            <Box className="a4-container">
              {shipments.map((shipment, index) => (
                <Paper
                  key={index}
                  elevation={1}
                  className="a4-label"
                  sx={{
                    width: "98mm",
                    height: "74.676mm",
                    padding: "2mm",
                    mx: "auto",
                    border: "1px solid #000",
                    overflow: "hidden",
                    boxSizing: "border-box",
                    mb: 2,
                  }}
                >
                  {/* Header Section */}
                  <div style={{ marginBottom: "2px", position: "relative", minHeight: "58px" }}>
                    {/* QR code + date - positioned absolutely so they don't affect layout height */}
                    <div
                      style={{
                        position: "absolute",
                        top: 0,
                        right: 0,
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        gap: "1px",
                      }}
                    >
                      <QRCode value={shipment.shipmentId} size={46} />
                      <span style={{ fontSize: "7px", fontWeight: 600, whiteSpace: "nowrap" }}>
                        {shipment.createdAt
                          ? new Date(shipment.createdAt).toLocaleDateString("en-GB", {
                            day: "2-digit",
                            month: "2-digit",
                            year: "numeric",
                          })
                          : "N/A"}
                      </span>
                    </div>
                    <div
                      style={{
                        display: "flex",
                        alignItems: "flex-start",
                        justifyContent: "space-between",
                        gap: "8px",
                        marginBottom: "0px",
                        paddingRight: "52px",
                      }}
                    >
                      <div
                        style={{
                          fontSize: "9px",
                          fontWeight: 600,
                          flex: 1,
                          wordBreak: "break-word",
                          lineHeight: "1.2",
                        }}
                      >
                        {shipment?.entity?.name || "N/A"}
                      </div>
                    </div>
                    <div
                      style={{
                        fontSize: "13px",
                        fontWeight: 700,
                        textAlign: "center",
                        lineHeight: 1.2,
                        marginTop: "2px",
                        marginBottom: "1px",
                      }}
                    >
                      {shipment?.shipmentId}
                    </div>
                    <div
                      style={{
                        display: "flex",
                        flexDirection: "column",
                        gap: "0px",
                        marginTop: "1px",
                      }}
                    >
                      {/* Branch Route */}
                      <div
                        style={{
                          fontSize: "12px",
                          fontWeight: 700,
                          textAlign: "center",
                          wordBreak: "break-word",
                          lineHeight: "1.2",
                        }}
                      >
                        {`${shipment?.sourceBranch?.name || "N/A"} → `}
                        <span style={{ fontWeight: 700, fontSize: "12px" }}>
                          {shipment?.destinationBranch?.name || "N/A"}
                        </span>
                      </div>
                    </div>
                  </div>
                  <Box my={0.1} borderBottom="1px dotted #000" />

                  {/* From / To Section */}
                  <Box
                    display="flex"
                    justifyContent="center"
                    fontSize={8}
                    mb={0.25}
                    lineHeight={1}
                  >
                    <Box width="48%">
                      <Typography fontWeight={700} fontSize={10} gutterBottom sx={{ mb: 0.5 }}>
                        From
                      </Typography>
                      {shipment.vendor ? (
                        <>
                          <InfoRow
                            text={
                              <span className="text-[9px] font-bold" style={{ fontSize: "9px" }}>
                                {`Name: ${shipment.vendor.shopName}`}
                              </span>
                            }
                          />
                          <InfoRow
                            text={
                              <span className="text-[9px] font-bold" style={{ fontSize: "9px" }}>
                                {`Contact: ${shipment.vendor.phone}`}
                              </span>
                            }
                          />
                          <InfoRow
                            text={
                              <span className="text-[9px] font-bold" style={{ fontSize: "9px" }}>
                                {`Address: ${shipment.vendor.address}`}
                              </span>
                            }
                          />
                          <InfoRow
                            text={
                              <span className="text-[9px] font-bold" style={{ fontSize: "9px" }}>
                                {`Payer (Sender): Rs. ${shipment.payerDetails?.sender?.totalCharge || 0
                                  }`}
                              </span>
                            }
                          />
                        </>
                      ) : (
                        <>
                          <InfoRow
                            text={
                              <span className="text-[9px] font-bold" style={{ fontSize: "9px" }}>
                                {`Name: ${shipment.customerId?.customerUser?.name || "N/A"}`}
                              </span>
                            }
                          />
                          <InfoRow
                            text={
                              <span className="text-[9px] font-bold" style={{ fontSize: "9px" }}>
                                {`Contact: ${shipment.customerId?.customerUser?.phone || "N/A"}`}
                              </span>
                            }
                          />
                          <InfoRow
                            text={
                              <span className="text-[9px] font-bold" style={{ fontSize: "9px" }}>
                                {`Address: ${shipment.customerId?.address || "N/A"}`}
                              </span>
                            }
                          />
                          <InfoRow
                            text={
                              <span className="text-[9px] font-bold" style={{ fontSize: "9px" }}>
                                {`Payer (Sender): Rs. ${shipment.payerDetails?.sender?.totalCharge || 0
                                  }`}
                              </span>
                            }
                          />
                        </>
                      )}
                    </Box>
                    <Box width="48%">
                      <Typography fontWeight={700} fontSize={10} gutterBottom sx={{ mb: 0.5 }}>
                        To
                      </Typography>
                      <InfoRow
                        text={
                          <span className="text-[9px] font-bold" style={{ fontSize: "9px" }}>
                            {`Name: ${shipment.receiver?.customerUser?.name || "N/A"}`}
                          </span>
                        }
                      />
                      <InfoRow
                        text={
                          <span className="text-[9px] font-bold" style={{ fontSize: "9px" }}>
                            {`Contact: ${shipment.receiver?.customerUser?.phone || "N/A"}`}
                          </span>
                        }
                      />
                      <InfoRow
                        text={
                          <span className="text-[9px] font-bold" style={{ fontSize: "9px" }}>
                            {`Address: ${shipment.receiver?.address || "N/A"}`}
                          </span>
                        }
                      />
                      <InfoRow
                        text={
                          <span className="text-[9px] font-bold" style={{ fontSize: "9px" }}>
                            {`Payer (Receiver): Rs. ${shipment.payerDetails?.receiver?.totalCharge || 0
                              }`}
                          </span>
                        }
                      />
                    </Box>
                  </Box>

                  <Box my={0.25} borderBottom="1px dotted #000" />

                  {/* Details Section */}
                  <Box className="" ml={1} mb={0.25}>
                    <Box
                      display="flex"
                      justifyContent="space-between"
                      alignItems="center"
                      lineHeight={1.2}
                    >
                      <span style={{ fontSize: "8px", fontWeight: 700 }}>Delivery Charge:</span>
                      <span style={{ fontSize: "8px", fontWeight: 700 }}>
                        Rs. {shipment.shipmentAmount || 0}
                      </span>
                    </Box>

                    <Box
                      display="flex"
                      justifyContent="space-between"
                      alignItems="center"
                      lineHeight={1.2}
                    >
                      <span style={{ fontSize: "8px", fontWeight: 700 }}>Shipment Weight:</span>
                      <span style={{ fontSize: "8px", fontWeight: 700 }}>
                        {shipment?.weight || 0} kg
                      </span>
                    </Box>

                    <Box
                      display="flex"
                      justifyContent="space-between"
                      alignItems="center"
                      lineHeight={1.2}
                    >
                      <span style={{ fontSize: "8px", fontWeight: 700 }}>Shipment Type:</span>
                      <span style={{ fontSize: "8px", fontWeight: 700 }}>
                        {shipment?.shipmentDelivery || "N/A"}
                      </span>
                    </Box>
                  </Box>

                  <Box borderBottom="1px dotted #000" />

                  {/* Description Section */}
                  <Box ml={1} my={0.25} lineHeight={1.1}>
                    <Box>
                      <span style={{ fontSize: "9px", fontWeight: 700 }}>Description:</span>
                      <span
                        style={{
                          fontWeight: 700,
                          fontSize: "8px",
                          marginLeft: "8px",
                          wordBreak: "break-word",
                        }}
                      >
                        {truncateDescription(shipment?.productDescription, 60)}
                      </span>
                    </Box>
                  </Box>
                  {/* Instruction Section */}
                  <Box ml={1} my={0.15} lineHeight={1.1}>
                    <Box>
                      <span style={{ fontSize: "9px", fontWeight: 700 }}>Instruction:</span>
                      <span
                        style={{
                          fontWeight: 700,
                          fontSize: "8px",
                          marginLeft: "8px",
                          wordBreak: "break-word",
                        }}
                      >
                        {truncateDescription(shipment?.instruction, 60)}
                      </span>
                    </Box>
                  </Box>

                  <Box my={0.1} borderBottom="1px dotted #000" />
                  {/* Barcode Section */}
                  <Box mt={0.25} textAlign="center" style={{ overflow: "hidden" }}>
                    {barcodeDataUrls[shipment?.reference || shipment.shipmentId] ? (
                      <>
                        <img
                          src={barcodeDataUrls[shipment?.reference || shipment.shipmentId]}
                          alt="Barcode"
                          style={{
                            width: "auto",
                            height: "auto",
                            maxHeight: "35px",
                            objectFit: "contain",
                            margin: "0 auto",
                            display: "block",
                          }}
                        />
                        <Typography fontSize={10} fontWeight={600} lineHeight={1} marginTop={0.5}>
                          {shipment?.reference || shipment.shipmentId}
                        </Typography>
                      </>
                    ) : (
                      <Typography fontSize={8}>Barcode not generated</Typography>
                    )}
                  </Box>
                </Paper>
              ))}
            </Box>
          )}
        </Box>
      </DialogContent>
      <DialogActions className="no-print">
        <Button
          onClick={onReopenPrintModal}
          sx={{
            borderRadius: "8px",
            textTransform: "none",
            "&:hover": {
              backgroundColor: "#f5f5f5",
            },
          }}
        >
          Back
        </Button>
        <Button
          variant="contained"
          onClick={handlePrint}
          sx={{
            borderRadius: "8px",
            textTransform: "none",
            boxShadow: "0 2px 8px rgba(25, 118, 210, 0.3)",
            "&:hover": {
              boxShadow: "0 4px 12px rgba(25, 118, 210, 0.4)",
            },
          }}
        >
          Print All ({printMode === "tsc" ? "TSC" : "A4"})
        </Button>
      </DialogActions>

      {/* ========== STYLES ========== */}
      {printMode === "tsc" ? (
        // TSC MODE - EXACT ORIGINAL CSS
        <style>{`
          @media print {
              @page { margin: 0; }
              .no-print { display: none !important; }
              .MuiPaper-root { 
               width: 103mm; 
                height: 74mm; 
                margin: auto !important;
                border: 1px solid #000;
                box-sizing: border-box !important;
                -webkit-print-color-adjust: exact !important;
                print-color-adjust: exact !important;
              }
              body { 
                display: flex;
                justify-content: center;
                align-items: center;
                min-height: 100vh;
                margin: 0;
                -webkit-print-color-adjust: exact; 
                print-color-adjust: exact; 
              }
              .MuiDialogContent-root { padding: 0 !important; }
              .MuiDialog-paper { margin: 0 !important; width: 100% !important; max-width: none !important; }
          }
        `}</style>
      ) : (
        // A4 MODE - NEW CSS
        <style>{`
          .a4-container {
            display: flex;
            flex-direction: column;
            align-items: center;
            max-width: 210mm;
            margin: 0 auto;
          }

          @media print {
            @page { 
              size: A4;
              margin: 15mm 10mm; 
            }
            
            .no-print { 
              display: none !important; 
            }
            
            body { 
              -webkit-print-color-adjust: exact; 
              print-color-adjust: exact; 
              margin: 0;
            }
            
            .MuiDialogContent-root { 
              padding: 0 !important; 
            }
            
            .MuiDialog-paper { 
              margin: 0 !important; 
              width: 100% !important; 
              max-width: none !important; 
            }

            .a4-container {
              display: flex !important;
              flex-direction: column !important;
              align-items: center !important;
              gap: 8mm !important;
              max-width: 210mm !important;
              margin: 0 auto !important;
            }

            .a4-label {
              width: 98mm !important;
              height: 74mm !important;
              margin: 0 auto !important;
              border: 1px solid #000 !important;
              box-sizing: border-box !important;
              -webkit-print-color-adjust: exact !important;
              print-color-adjust: exact !important;
              page-break-inside: avoid !important;
            }

            .a4-label:nth-child(3n) {
              page-break-after: always !important;
            }

            .a4-label:last-child {
              page-break-after: auto !important;
            }
          }
        `}</style>
      )}
    </Dialog>
  );
};

export default MultiShipmentLabel;
